import { StageDef } from '../automationMachineInterfaces'
import {
  getCurrentValueForRadioButtons,
  saveCurrentValueForRadioButtons,
  toggleRadioButtonWithWebRequest,
} from './radioButtonUtils'
import { STRAVA_AUTOMATION_STAGE_STATUSES } from './stravaAutomationStageStatuses'
import * as stravaUrls from './stravaUrls'
import { StravaSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

const sectionTitle = 'Where You Appear'

const MENU_TITLES = {
  MENTIONS: 'Mentions',
}

const MENTIONS_SETTINGS = {
  NO_ONE: 'No One',
  FOLLOWERS: 'Followers',
  EVERYONE: 'Everyone',
}

const MENTIONS_WEB_REQUEST_PAYLOAD = {
  [MENTIONS_SETTINGS.NO_ONE]: { mentions_visibility: 'no_one' },
  [MENTIONS_SETTINGS.FOLLOWERS]: { mentions_visibility: 'followers_only' },
  [MENTIONS_SETTINGS.EVERYONE]: { mentions_visibility: 'everyone' },
}

// Settings properties
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => value)

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(
    key,
    (value) => value === 'Followers' || value === 'No One',
  )

const getCantUndoFn = () => defaultCantUndoFn

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// Setup gather stage definitions

enum StageKey {
  GATHER_SETTING_MENTIONS_VISIBILITY = 'GATHER_SETTING_MENTIONS_VISIBILITY',
  UPDATE_SETTING_MENTIONS_VISIBILITY = 'UPDATE_SETTING_MENTIONS_VISIBILITY',
  UNDO_SETTING_MENTIONS_VISIBILITY = 'UNDO_SETTING_MENTIONS_VISIBILITY',
  UPDATE_SETTING_MENTIONS_FINALIZE = 'UPDATE_SETTING_MENTIONS_FINALIZE',
  UNDO_SETTING_MENTIONS_FINALIZE = 'UNDO_SETTING_MENTIONS_FINALIZE',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_MENTIONS_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForRadioButtons(
        userContext.updateStoredInitialValues,
        MENTIONS_SETTINGS,
        StravaSettingKey.MENTIONS_SETTING,
        enums.StravaRecommendationKey.STRAVA_MENTIONS,
        sectionTitle,
        MENU_TITLES.MENTIONS,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_MENTIONS_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry, userId } = userContext

      // Skip if not selected
      if (
        !settingsSelected ||
        !settingsSelected[StravaSettingKey.MENTIONS_SETTING]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForRadioButtons(
        sectionTitle,
        MENU_TITLES.MENTIONS,
        MENTIONS_SETTINGS,
      )

      // Toggle setting if incorrect one is selected
      if (
        !isDry &&
        (currentValue !== MENTIONS_SETTINGS.NO_ONE ||
          currentValue !== MENTIONS_SETTINGS.FOLLOWERS)
      ) {
        await toggleRadioButtonWithWebRequest(userId, {
          athlete: MENTIONS_WEB_REQUEST_PAYLOAD[MENTIONS_SETTINGS.FOLLOWERS],
        })
      }
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_MENTIONS_FINALIZE,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async () => {
      window.location.reload()
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_MENTIONS_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry, userId } = userContext
      // Skip if not selected
      if (!initialValues || !initialValues[StravaSettingKey.MENTIONS_SETTING]) {
        return null
      }

      const currentValue = await getCurrentValueForRadioButtons(
        sectionTitle,
        MENU_TITLES.MENTIONS,
        MENTIONS_SETTINGS,
      )

      // Toggle setting if incorrect one is selected
      if (
        !isDry &&
        currentValue !== initialValues[StravaSettingKey.MENTIONS_SETTING]
      ) {
        await toggleRadioButtonWithWebRequest(userId, {
          athlete:
            MENTIONS_WEB_REQUEST_PAYLOAD[
              initialValues[StravaSettingKey.MENTIONS_SETTING]
            ],
        })
      }
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_MENTIONS_FINALIZE,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async () => {
      window.location.reload()
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
